import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import {
  FaEnvelope,
  FaTimes,
  FaPaperPlane,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { API_BASE } from "../../../ApiUrl";
import "./Invitation.css";

const REGISTER_LINK = "http://145.79.0.94:81/";

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const formatDateTime = (value) => {
  if (!value) return "—";

  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Invitation = () => {
  const [invitations, setInvitations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    setListLoading(true);

    try {
      const res = await axios.get(
        `${API_BASE}/tenant-reg-invitations/`
      );

      const data = res.data?.data;

      setInvitations(
        Array.isArray(data) ? data : data ? [data] : []
      );

      setCurrentPage(1);
    } catch (err) {
      console.error("Error fetching invitations", err);
    } finally {
      setListLoading(false);
    }
  };

  const addEmail = (rawValue) => {
    const value = rawValue.trim().replace(/,$/, "");

    if (!value) return;

    if (!isValidEmail(value)) {
      setError(`"${value}" is not a valid email address`);
      return false;
    }

    if (emails.includes(value)) {
      setError(`"${value}" has already been added`);
      return false;
    }

    setEmails((prev) => [...prev, value]);
    setEmailInput("");
    setError("");

    return true;
  };

  const handleInputKeyDown = (e) => {
    if (["Enter", ",", "Tab"].includes(e.key)) {
      if (emailInput.trim()) {
        e.preventDefault();
        addEmail(emailInput);
      }
    } else if (
      e.key === "Backspace" &&
      !emailInput &&
      emails.length > 0
    ) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const handleInputBlur = () => {
    if (emailInput.trim()) {
      addEmail(emailInput);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");

    if (/[,\s;]/.test(pasted)) {
      e.preventDefault();

      const parts = pasted
        .split(/[,\s;]+/)
        .filter(Boolean);

      parts.forEach((part) => addEmail(part));
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmails((prev) =>
      prev.filter((email) => email !== emailToRemove)
    );
  };

  const resetModalState = () => {
    setEmailInput("");
    setEmails([]);
    setError("");
  };

  const closeModal = () => {
    setShowModal(false);
    resetModalState();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    let finalEmails = [...emails];

    if (emailInput.trim()) {
      const value = emailInput.trim();

      if (!isValidEmail(value)) {
        setError(`"${value}" is not a valid email address`);
        return;
      }

      if (!finalEmails.includes(value)) {
        finalEmails.push(value);
      }
    }

    if (finalEmails.length === 0) {
      setError(
        "Add at least one tenant email before sending invitations."
      );
      return;
    }

    setLoading(true);

    try {
      const payload =
        finalEmails.length === 1
          ? {
              email: finalEmails[0],
              registration_url: REGISTER_LINK,
            }
          : finalEmails.map((email) => ({
              email,
              registration_url: REGISTER_LINK,
            }));

      await axios.post(
        `${API_BASE}/tenant-reg-invitations/`,
        payload
      );

      Swal.fire({
        icon: "success",
        title: "Invitation Sent",
        text: `Invitation${
          finalEmails.length > 1 ? "s" : ""
        } sent to ${finalEmails.length} tenant${
          finalEmails.length > 1 ? "s" : ""
        } successfully.`,
        confirmButtonColor: "#2563eb",
      });

      closeModal();
      fetchInvitations();
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Failed to Send Invitation",
        text:
          err.response?.data?.message ||
          "Something went wrong while sending invitations. Please try again.",
        confirmButtonColor: "#2563eb",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Pagination ---------------- */

  const totalEntries = invitations.length;

  const totalPages = Math.ceil(
    totalEntries / entriesPerPage
  );

  const startIndex =
    totalEntries === 0
      ? 0
      : (currentPage - 1) * entriesPerPage;

  const endIndex = Math.min(
    startIndex + entriesPerPage,
    totalEntries
  );

  const currentInvitations = invitations.slice(
    startIndex,
    endIndex
  );

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="invite-container">
          <div className="invite-list-card">
            <div className="invite-list-card__header">
              <div>
                <h2 className="invite-list-card__title">
                  Tenant Invitations
                </h2>

                <p className="invite-list-card__subtitle">
                  Track invitations sent to prospective tenants.
                </p>
              </div>

              <button
                type="button"
                className="invite-form__submit"
                onClick={() => setShowModal(true)}
              >
                <FaPlus />
                Send Invitation
              </button>
            </div>

            <div className="card-body--table">
              {listLoading ? (
                <div className="invite-empty">
                  Loading invitations...
                </div>
              ) : (
                <>
                  <div className="invite-table-wrapper">
                    <table className="invite-table">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Email</th>
                          <th>Invitation Date &amp; Time</th>
                          <th>Registered</th>
                        </tr>
                      </thead>

                      <tbody>
                        {currentInvitations.length > 0 ? (
                          currentInvitations.map(
                            (inv, index) => (
                              <tr
                                key={
                                  inv.id ??
                                  `${startIndex}-${index}`
                                }
                              >
                                <td>
                                  {startIndex + index + 1}
                                </td>

                                <td className="invite-table-email">
                                  <div className="invite-table-email-cell">
                                    <div className="invite-table-email-icon">
                                      <FaEnvelope />
                                    </div>

                                    <span>
                                      {inv.email}
                                    </span>
                                  </div>
                                </td>

                                <td className="invite-table-date">
                                  {formatDateTime(
                                    inv.invitation_date_time
                                  )}
                                </td>

                                <td>
                                  <span
                                    className={`invite-table-status ${
                                      inv.is_registered
                                        ? "invite-table-status-registered"
                                        : "invite-table-status-pending"
                                    }`}
                                  >
                                    <span className="invite-table-status-dot"></span>

                                    {inv.is_registered
                                      ? "Registered"
                                      : "Pending"}
                                  </span>
                                </td>
                              </tr>
                            )
                          )
                        ) : (
                          <tr>
                            <td
                              colSpan="4"
                              className="invite-table-no-data"
                            >
                              No invitations sent yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="invite-table-pagination">
                    <div className="invite-table-pagination-left">
                      <span>Show</span>

                      <select
                        value={entriesPerPage}
                        onChange={handleEntriesChange}
                        className="invite-table-entries-select"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>

                      <span>entries</span>
                    </div>

                    <div className="invite-table-pagination-right">
                      <span className="invite-table-showing-text">
                        Showing{" "}
                        <strong>
                          {totalEntries === 0
                            ? 0
                            : startIndex + 1}
                        </strong>{" "}
                        to{" "}
                        <strong>{endIndex}</strong>{" "}
                        of{" "}
                        <strong>{totalEntries}</strong>{" "}
                        entries
                      </span>

                      <div className="invite-table-pagination-controls">
                        <button
                          type="button"
                          className="invite-table-pagination-btn"
                          onClick={() =>
                            goToPage(currentPage - 1)
                          }
                          disabled={currentPage === 1}
                          title="Previous"
                        >
                          <FaChevronLeft />
                        </button>

                        {getPageNumbers().map(
                          (page, index) =>
                            page === "..." ? (
                              <span
                                key={`dots-${index}`}
                                className="invite-table-pagination-dots"
                              >
                                ...
                              </span>
                            ) : (
                              <button
                                type="button"
                                key={page}
                                className={`invite-table-pagination-btn ${
                                  currentPage === page
                                    ? "invite-table-pagination-active"
                                    : ""
                                }`}
                                onClick={() =>
                                  goToPage(page)
                                }
                              >
                                {page}
                              </button>
                            )
                        )}

                        <button
                          type="button"
                          className="invite-table-pagination-btn"
                          onClick={() =>
                            goToPage(currentPage + 1)
                          }
                          disabled={
                            currentPage === totalPages ||
                            totalPages === 0
                          }
                          title="Next"
                        >
                          <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send Invitation Modal */}

      {showModal && (
        <div
          className="invite-modal-overlay"
          onClick={closeModal}
        >
          <div
            className="invite-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="invite-card__header">
              <div className="invite-card__icon">
                <FaPaperPlane />
              </div>

              <div>
                <h2 className="invite-card__title">
                  Invite Tenants
                </h2>

                <p className="invite-card__subtitle">
                  Send registration invitations to one or more
                  tenants via email.
                </p>
              </div>

              <button
                type="button"
                className="invite-modal__close"
                onClick={closeModal}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <form
              className="invite-form"
              onSubmit={handleSubmit}
            >
              <div className="invite-form__group">
                <label className="invite-form__label">
                  Tenant Email(s){" "}
                  <span className="invite-form__required">
                    *
                  </span>
                </label>

                <div
                  className="invite-email-input"
                  onClick={() =>
                    document
                      .getElementById("invite-email-field")
                      ?.focus()
                  }
                >
                  {emails.map((email) => (
                    <span
                      className="invite-email-chip"
                      key={email}
                    >
                      <FaEnvelope className="invite-email-chip__icon" />

                      {email}

                      <button
                        type="button"
                        className="invite-email-chip__remove"
                        onClick={() =>
                          removeEmail(email)
                        }
                        aria-label={`Remove ${email}`}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}

                  <input
                    id="invite-email-field"
                    type="text"
                    className="invite-email-input__field"
                    placeholder={
                      emails.length === 0
                        ? "Enter email and press Enter or comma (e.g. tenant@company.com)"
                        : "Add another email…"
                    }
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);

                      if (error) {
                        setError("");
                      }
                    }}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    onPaste={handlePaste}
                  />
                </div>

                <small className="invite-form__hint">
                  Press <strong>Enter</strong>,{" "}
                  <strong>comma</strong>, or paste a list of
                  emails to add multiple tenants.
                </small>

                {error && (
                  <span className="invite-form__error">
                    {error}
                  </span>
                )}
              </div>

              <div className="invite-form__actions">
                <span className="invite-form__count">
                  {emails.length} tenant
                  {emails.length !== 1 ? "s" : ""} added
                </span>

                <button
                  type="submit"
                  className="invite-form__submit"
                  disabled={loading}
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <FaPaperPlane />
                      Send Invitation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invitation;